import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useSnackbar } from "notistack";
import Layout from "../Layout/Layout";
import { APIUrl } from "../../Helper/APIUrl";
import { handleKeyPress, handleKeyPressString } from "../../Helper/Helper";

function AddTestimonial() {
    const Token = JSON.parse(localStorage.getItem("tcn"));
    const localStorageUserId = JSON.parse(localStorage.getItem("userid"));
    const { enqueueSnackbar } = useSnackbar();

    const [search, setSearch] = useState("");
    const [testimonials, setTestimonials] = useState([]);
    const [filteredTestimonials, setFilteredTestimonials] = useState([]);

    const [Id, setId] = useState(0);
    const [Name, setName] = useState("");
    const [Photo, setPhoto] = useState("");
    const [AltText, setAltText] = useState("");
    const [ShortDescription, setShortDescription] = useState("");
    const [Profile, setProfile] = useState("");
    const [Rating, setRating] = useState("");
    const [ImageFile, setImageFile] = useState(null);
    const [ImagePreview, setImagePreview] = useState("");
    const [IsActive, setIsActive] = useState(true);
    const [edit, setEdit] = useState(false);

    const NameRef = useRef(null);
    const ProfileRef = useRef(null);
    const RatingRef = useRef(null);
    const AltTextRef = useRef(null);
    const ImageRef = useRef(null);

    const parseJSON = async (response) => {
        try {
            const text = await response.text();
            if (!text) return null;
            return JSON.parse(text);
        } catch (err) {
            console.warn("Failed to parse JSON response", err);
            return null;
        }
    };

    const getImageSource = (imagePath) => {
        if (!imagePath) return "";
        if (String(imagePath).startsWith("http")) return imagePath;
        return `${APIUrl.BaseUrl}${imagePath}`;
    };

    const doEmptyStates = () => {
        if (ImagePreview && ImagePreview.startsWith("blob:")) {
            URL.revokeObjectURL(ImagePreview);
        }

        setId(0);
        setName("");
        setPhoto("");
        setAltText("");
        setShortDescription("");
        setProfile("");
        setRating("");
        setImageFile(null);
        setImagePreview("");
        setIsActive(true);
        setEdit(false);

        if (ImageRef.current) {
            ImageRef.current.value = "";
        }
    };

    const getAllTestimonial = async () => {
        try {
            const response = await fetch(APIUrl.Testimonial + "GetAllTestimonial", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Token}`,
                },
                body: JSON.stringify({}),
            });

            const result = await parseJSON(response);
            if (result && Array.isArray(result.data)) {
                setTestimonials(result.data);
                setFilteredTestimonials(result.data);
            } else {
                setTestimonials([]);
                setFilteredTestimonials([]);
            }
        } catch (error) {
            console.log(error);
            setTestimonials([]);
            setFilteredTestimonials([]);
        }
    };

    useEffect(() => {
        if (!Token) {
            enqueueSnackbar("Please login first", { variant: "warning" });
            return;
        }

        getAllTestimonial();
    }, []);

    useEffect(() => {
        const txt = search.toLowerCase();
        const result = testimonials.filter((x) => {
            const name = (x.Name || "").toLowerCase();
            const profile = (x.Profile || "").toLowerCase();
            const shortDescription = (x.ShortDescription || "").toLowerCase();
            return name.includes(txt) || profile.includes(txt) || shortDescription.includes(txt);
        });

        setFilteredTestimonials(result);
    }, [search, testimonials]);

    useEffect(() => {
        return () => {
            if (ImagePreview && ImagePreview.startsWith("blob:")) {
                URL.revokeObjectURL(ImagePreview);
            }
        };
    }, [ImagePreview]);

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const isValidType = ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type);
        if (!isValidType) {
            enqueueSnackbar("Only JPG, JPEG, PNG, or WEBP files are allowed.", { variant: "warning" });
            event.target.value = "";
            return;
        }

        if (ImagePreview && ImagePreview.startsWith("blob:")) {
            URL.revokeObjectURL(ImagePreview);
        }

        const preview = URL.createObjectURL(file);
        setImageFile(file);
        setImagePreview(preview);
    };

    const handleRatingChange = (event) => {
        const rawValue = String(event.target.value || "");
        const nextValue = rawValue.replace(/\D/g, "").slice(0, 1);

        if (rawValue && nextValue && Number(nextValue) > 5) {
            enqueueSnackbar("Rating must be between 1 and 5.", { variant: "warning" });
            setRating("");
            return;
        }

        if (rawValue && !nextValue) {
            enqueueSnackbar("Rating must be between 1 and 5.", { variant: "warning" });
            setRating("");
            return;
        }

        if (nextValue && Number(nextValue) < 1) {
            enqueueSnackbar("Rating must be between 1 and 5.", { variant: "warning" });
            setRating("");
            return;
        }

        setRating(nextValue);
    };

    const validateForm = () => {
        if (!Name || Name.trim().length < 2) {
            enqueueSnackbar("Name is required.", { variant: "warning" });
            NameRef.current?.focus();
            return false;
        }

        if (!Profile || Profile.trim().length < 2) {
            enqueueSnackbar("Profile is required.", { variant: "warning" });
            ProfileRef.current?.focus();
            return false;
        }

        if (!Rating || String(Rating).trim().length < 1) {
            enqueueSnackbar("Rating is required.", { variant: "warning" });
            RatingRef.current?.focus();
            return false;
        }

        if (!/^[1-5]$/.test(String(Rating))) {
            enqueueSnackbar("Rating must be a number between 1 and 5.", { variant: "warning" });
            RatingRef.current?.focus();
            return false;
        }

        if (!AltText || AltText.trim().length < 2) {
            enqueueSnackbar("Alt Text is required.", { variant: "warning" });
            AltTextRef.current?.focus();
            return false;
        }

        if (!ShortDescription || ShortDescription.trim().length < 3) {
            enqueueSnackbar("Short Description is required.", { variant: "warning" });
            return false;
        }

        if (!edit && !ImageFile) {
            enqueueSnackbar("Please upload photo.", { variant: "warning" });
            ImageRef.current?.focus();
            return false;
        }

        return true;
    };

    const insertTestimonial = async () => {
        try {
            if (!validateForm()) return;

            const payload = {
                Name,
                Photo: "",
                AltText,
                ShortDescription,
                Profile,
                Rating,
                IsActive: true,
                CreatedBy: localStorageUserId || 0,
                ModifyBy: localStorageUserId || 0,
            };

            const formData = new FormData();
            formData.append("TestimonialJson", JSON.stringify(payload));

            if (ImageFile) {
                formData.append("Image", ImageFile);
            }

            const response = await fetch(APIUrl.Testimonial + "InsertTestimonial", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
                body: formData,
            });

            const result = await parseJSON(response);

            if (response.ok && result) {
                enqueueSnackbar("Successfully Added", { variant: "success" });
                getAllTestimonial();
                doEmptyStates();
            } else {
                enqueueSnackbar(result?.Message || "Failed to add Testimonial", { variant: "error" });
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar("Error adding Testimonial", { variant: "error" });
        }
    };

    const updateTestimonial = async (_Id) => {
        try {
            if (!validateForm()) return;

            const payload = {
                Id: _Id,
                Name,
                Photo,
                AltText,
                ShortDescription,
                Profile,
                Rating,
                IsActive,
                CreatedBy: localStorageUserId || 0,
                ModifyBy: localStorageUserId || 0,
            };

            const formData = new FormData();
            formData.append("TestimonialJson", JSON.stringify(payload));

            if (ImageFile) {
                formData.append("Image", ImageFile);
            }

            const response = await fetch(APIUrl.Testimonial + "UpdateTestimonial/" + _Id, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${Token}`,
                },
                body: formData,
            });

            const result = await parseJSON(response);

            if (response.ok && result) {
                enqueueSnackbar("Successfully Updated", { variant: "success" });
                getAllTestimonial();
                doEmptyStates();
            } else {
                enqueueSnackbar(result?.Message || "Failed to update Testimonial", { variant: "error" });
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar("Error updating Testimonial", { variant: "error" });
        }
    };

    const editTestimonial = (row) => {
        setId(row.Id);
        setName(row.Name || "");
        setPhoto(row.Photo || "");
        setAltText(row.AltText || "");
        setShortDescription(row.ShortDescription || "");
        setProfile(row.Profile || "");
        setRating(row.Rating || "");
        setImageFile(null);
        setImagePreview(getImageSource(row.Photo));
        setIsActive(row.IsActive ?? true);
        setEdit(true);

        if (ImageRef.current) {
            ImageRef.current.value = "";
        }

        NameRef.current?.focus();
    };

    const activateDeactivateTestimonial = async (_Id, Value) => {
        try {
            const response = await fetch(APIUrl.Testimonial + "ActivateDeactivateTestimonial/" + _Id, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Token}`,
                },
                body: JSON.stringify({ Value }),
            });

            const result = await parseJSON(response);

            if (response.ok && result) {
                enqueueSnackbar(Value === "activate" ? "Successfully Activated" : "Successfully Deactivated", {
                    variant: Value === "activate" ? "success" : "warning",
                });
                getAllTestimonial();
            } else {
                enqueueSnackbar(result?.Message || "Failed to update status", { variant: "error" });
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar("Error updating status", { variant: "error" });
        }
    };

    const columns = [
        {
            name: "SNo.",
            selector: (_, index) => index + 1,
            maxWidth: "70px",
        },
        {
            name: "Name",
            selector: (row) => row.Name,
            sortable: true,
            grow: 1,
        },
        {
            name: "Photo",
            grow: 1,
            cell: (row) =>
                row.Photo ? (
                    <img
                        src={getImageSource(row.Photo)}
                        alt={row.AltText || row.Name || "Testimonial"}
                        className="h-16 w-24 object-cover rounded-md border border-gray-200 my-2"
                    />
                ) : (
                    <span className="text-xs text-gray-500">No image</span>
                ),
        },
        {
            name: "Profile",
            selector: (row) => row.Profile,
            sortable: true,
            grow: 1,
        },
        {
            name: "Rating",
            selector: (row) => row.Rating,
            sortable: true,
            maxWidth: "100px",
        },
        {
            name: "Short Description",
            selector: (row) => row.ShortDescription,
            sortable: true,
            grow: 2,
            wrap: true,
        },
        {
            name: "Status",
            cell: (row) =>
                row.IsActive ? (
                    <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Activated</label>
                ) : (
                    <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Deactivated</label>
                ),
            grow: 1,
        },
        {
            name: "Action",
            grow: 1,
            cell: (row) => (
                <div className="flex items-center gap-2 py-2">
                    <Button
                        onClick={() => editTestimonial(row)}
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2"
                        title="Edit Testimonial"
                    >
                        <i className="fa fa-pencil" aria-hidden="true"></i>
                    </Button>

                    {row.IsActive ? (
                        <Button
                            onClick={() => activateDeactivateTestimonial(row.Id, "deactivate")}
                            className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-2"
                            title="Deactivate Testimonial"
                        >
                            <i className="fa fa-toggle-on" aria-hidden="true"></i>
                        </Button>
                    ) : (
                        <Button
                            onClick={() => activateDeactivateTestimonial(row.Id, "activate")}
                            className="text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-3 py-2"
                            title="Activate Testimonial"
                        >
                            <i className="fa fa-toggle-off" aria-hidden="true"></i>
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <Layout>
            <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Manage Testimonial</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                <input
                                    ref={NameRef}
                                    type="text"
                                    value={Name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter Name"
                                    onKeyDown={handleKeyPressString}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Profile</label>
                                <input
                                    ref={ProfileRef}
                                    type="text"
                                    value={Profile}
                                    onChange={(e) => setProfile(e.target.value)}
                                    placeholder="Enter Profile"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    onKeyDown={handleKeyPressString}

                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Rating out of 5</label>
                                <input
                                    ref={RatingRef}
                                    onKeyDown={handleKeyPress}
                                    inputMode="numeric"
                                    maxLength={1}
                                    type="text"
                                    value={Rating}
                                    onChange={handleRatingChange}
                                    placeholder="Enter Rating"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
                                <input
                                    ref={AltTextRef}
                                    type="text"
                                    value={AltText}
                                    onChange={(e) => setAltText(e.target.value)}
                                    placeholder="Enter Alt Text"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Photo Upload</label>
                                <input
                                    ref={ImageRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>

                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
                                <textarea
                                    value={ShortDescription}
                                    onChange={(e) => setShortDescription(e.target.value)}
                                    rows={3}
                                    placeholder="Enter Short Description"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                        </div>

                        {ImagePreview && (
                            <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 mb-2">Photo Preview</p>
                                <img
                                    src={ImagePreview}
                                    alt="Preview"
                                    className="h-28 w-44 object-cover rounded-md border border-gray-200"
                                />
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-4 mt-6">
                            {!edit ? (
                                <Button
                                    onClick={insertTestimonial}
                                    className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    Add
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => updateTestimonial(Id)}
                                    className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    Update
                                </Button>
                            )}

                            <button
                                type="button"
                                onClick={doEmptyStates}
                                className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <DataTable
                                title="Get All Testimonial"
                                columns={columns}
                                data={filteredTestimonials}
                                pagination
                                fixedHeader
                                fixedHeaderScrollHeight="550px"
                                highlightOnHover
                                subHeader
                                subHeaderAlign="right"
                                subHeaderComponent={
                                    <input
                                        type="text"
                                        placeholder="Search name / profile / description"
                                        className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                }
                            />
                        </div>
                    </div>
                </div>
            </main>
        </Layout>
    );
}

export default AddTestimonial;
